/**
 * LandingScreen - Mode selection screen for unified widget mode
 * 
 * Required from parent (widget):
 * - config: { landing, header, language, ... }
 * - translations: object
 * - shadowRoot: ShadowRoot | Document
 */

export class LandingScreen {
  constructor(config) {
    this.config = config;
    this.translations = config.translations || {};
    this.shadowRoot = config.shadowRoot;
  }

  /**
   * Helper to get translated text
   */
  t(key) {
    const lang = this.config.language || 'en';
    const translations = this.translations[lang] || this.translations.en || {};
    return translations[key] || key;
  }

  /**
   * Generate HTML for landing screen
   */
  generateHTML() {
    const landing = this.config.landing || {};
    
    // Determine logo content based on logoType
    let logoContent = '';
    if (landing.logoType === 'image' && landing.logoImageUrl) {
      logoContent = `<img src="${landing.logoImageUrl}" alt="Logo" style="max-width: 44px; max-height: 44px; object-fit: contain; border: none; outline: none;">`;
    } else {
      logoContent = `<span class="landing-avatar-emoji">${landing.logoIcon || landing.logo || '🤖'}</span>`;
    }
    
    return `
      <div class="landing-screen" id="landingScreen">
        <!-- Avatar -->
        <div class="landing-avatar">
          ${logoContent}
        </div>

        <!-- Title -->
        <h2 class="landing-title">${landing.title || this.t('landingTitle')}</h2>
        <p class="landing-subtitle">${landing.subtitle || this.t('landingSubtitle') || 'Choose your preferred method'}</p>

        <!-- Mode Selection - Side by Side -->
        <div class="mode-selection">
          <!-- Voice Call Option -->
          <button class="mode-card" id="mode-card-voice">
            <div class="mode-card-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/>
                <path d="M19 10v2a7 7 0 0 1-14 0v-2"/>
                <line x1="12" y1="19" x2="12" y2="23"/>
                <line x1="8" y1="23" x2="16" y2="23"/>
              </svg>
            </div>
            <span class="mode-card-title">${landing.voiceCardTitle || this.t('voiceCall')}</span>
          </button>

          <!-- Text Chat Option -->
          <button class="mode-card" id="mode-card-text">
            <div class="mode-card-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/>
              </svg>
            </div>
            <span class="mode-card-title">${landing.textCardTitle || this.t('textChat')}</span>
          </button>
        </div>
      </div>
    `;
  }

  /**
   * Generate CSS for landing screen
   */
  generateCSS() {
    const landing = this.config.landing || {};
    const anim = this.config.animation || {};
    
    // Add !important when not using Shadow DOM
    const important = this.config.useShadowDOM === false ? ' !important' : '';

    return `
      /* ===== LANDING SCREEN - New Design ===== */
      .landing-screen { 
        display: none${important}; 
        flex: 1; 
        padding: 32px 24px; 
        background: ${landing.backgroundColor || 'linear-gradient(180deg, #ffffff 0%, rgba(168, 85, 247, 0.03) 100%)'}; 
        align-items: center; 
        justify-content: flex-start; 
        flex-direction: column; 
        gap: 0; 
        overflow-y: auto; 
        min-height: 0;
        text-align: center;
      }
      
      .landing-screen.active { 
        display: flex${important}; 
      }

      /* Avatar - Richer purple gradient */
      .landing-avatar {
        width: 88px;
        height: 88px;
        border-radius: 22px;
        margin-bottom: 20px;
        display: flex;
        align-items: center;
        justify-content: center;
        ${(() => {
          // Determine background based on logo type and background settings
          if (landing.logoType === 'image') {
            // For image logos, use logoBackgroundColor if enabled, otherwise transparent
            if (landing.logoBackgroundEnabled !== false) {
              const bgColor = landing.logoBackgroundColor || landing.avatarBackground || 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)';
              return `background: ${bgColor}; box-shadow: 0 8px 28px rgba(102, 126, 234, 0.35); border: none;`;
            } else {
              return `background: transparent; box-shadow: none; border: none; outline: none;`;
            }
          } else {
            // For icon logos, use avatarBackground (default behavior)
            const bgColor = landing.avatarBackground || 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)';
            return `background: ${bgColor}; box-shadow: 0 8px 28px rgba(102, 126, 234, 0.35); border: none;`;
          }
        })()}
      }
      
      .landing-avatar img {
        border: none !important;
        outline: none !important;
      }

      .landing-avatar-emoji {
        font-size: 44px;
        line-height: 1;
      }

      /* Title & Subtitle */
      .landing-title { 
        font-size: 20px; 
        color: ${landing.titleColor || '#1e1b4b'}; 
        font-weight: 600; 
        margin-bottom: 6px; 
        text-align: center;
        line-height: 1.3;
      }

      .landing-subtitle {
        font-size: 14px;
        color: ${landing.subtitleColor || '#64748b'};
        margin-bottom: 28px;
        text-align: center;
      }

      /* Mode Selection - Side by Side */
      .mode-selection { 
        display: flex; 
        gap: 12px; 
        width: 100%; 
        justify-content: center; 
      }

      .mode-card { 
        flex: 1; 
        max-width: 160px; 
        padding: 20px 16px;
        background: ${landing.modeCardBackgroundColor || '#ffffff'}; 
        border: 1px solid ${landing.modeCardBorderColor || 'rgba(0, 0, 0, 0.06)'}; 
        border-radius: 18px; 
        cursor: pointer; 
        display: flex; 
        flex-direction: column; 
        align-items: center; 
        gap: 12px; 
        transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1); 
        box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06); 
        font-family: inherit;
      }

      .mode-card:hover { 
        transform: translateY(-4px); 
        box-shadow: 0 8px 24px rgba(124, 58, 237, 0.2); 
        border-color: rgba(124, 58, 237, 0.3); 
      }

      .mode-card:active {
        transform: translateY(-2px);
      }

      /* Mode Card Icon - Vibrant purple gradient */
      .mode-card-icon { 
        width: 56px; 
        height: 56px; 
        display: flex; 
        align-items: center; 
        justify-content: center; 
        border-radius: 16px; 
        background: ${landing.modeCardIconBackgroundColor || 'linear-gradient(135deg, #7c3aed, #a855f7)'}; 
        color: #fff; 
        box-shadow: 0 4px 14px rgba(124, 58, 237, 0.35);
      }

      .mode-card-icon svg {
        width: 26px;
        height: 26px;
        stroke: white;
        fill: none;
      }

      .mode-card-title { 
        color: ${landing.modeCardTitleColor || '#1e1b4b'}; 
        font-weight: 600; 
        font-size: 14px;
        text-align: center;
      }

      /* Mobile Responsive */
      @media (max-width: 768px) {
        .landing-screen {
          padding: 24px 16px;
        }

        .landing-avatar {
          width: 72px;
          height: 72px;
          border-radius: 18px;
          margin-bottom: 16px;
        }

        .landing-avatar-emoji {
          font-size: 36px;
        }

        .landing-title {
          font-size: 18px;
        }

        .landing-subtitle {
          font-size: 13px;
          margin-bottom: 20px;
        }

        .mode-selection {
          gap: 10px;
        }

        .mode-card {
          padding: 16px 12px;
          max-width: 140px;
        }

        .mode-card-icon {
          width: 48px;
          height: 48px;
          border-radius: 14px;
        }

        .mode-card-icon svg {
          width: 22px;
          height: 22px;
        }

        .mode-card-title {
          font-size: 13px;
        }
      }

      @media (max-width: 380px) {
        .mode-selection {
          flex-direction: column;
          align-items: center;
        }

        .mode-card {
          max-width: 100%;
          width: 100%;
          flex-direction: row;
          padding: 14px 18px;
          gap: 14px;
        }

        .mode-card-icon {
          flex-shrink: 0;
        }

        .mode-card-title {
          text-align: left;
        }
      }
    `;
  }

  /**
   * Show the landing screen
   */
  show() {
    const landing = this.shadowRoot?.getElementById('landingScreen');
    if (landing) {
      landing.classList.add('active');
    }
  }

  /**
   * Hide the landing screen
   */
  hide() {
    const landing = this.shadowRoot?.getElementById('landingScreen');
    if (landing) {
      landing.classList.remove('active');
    }
  }

  /**
   * Setup event handlers (called by parent widget)
   */
  setupEventHandlers(callbacks = {}) {
    const voiceCard = this.shadowRoot?.getElementById('mode-card-voice');
    const textCard = this.shadowRoot?.getElementById('mode-card-text');

    if (voiceCard && callbacks.onSelectVoice) {
      voiceCard.onclick = callbacks.onSelectVoice;
    }

    if (textCard && callbacks.onSelectText) {
      textCard.onclick = callbacks.onSelectText;
    }
  }
}